import { useState } from 'react';

import { JobProcessStatus } from 'models/JobProcess';
import { JOB_PROCESS_CHANNEL } from 'services/api/constants';

import { useWebsocketSubscription } from './useWebsocketSubscription';

type JobProcessPayload = {
  id: string;
  status: string;
  error: string | null;
  context: Record<string, unknown>;
  type: string;
};

export type JobProgress = { processed: number; total: number } & Record<string, number>;

/**
 * Subscribes to a JobProcess by id and surfaces its `context.progress` and
 * status. Calls `onFinished` once the process reaches a terminal state.
 */
export default function useJobProcessProgress(jobProcessId?: string | null, onFinished?: () => void) {
  const [progress, setProgress] = useState<JobProgress | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const { subscription } = useWebsocketSubscription<JobProcessPayload>({
    channel: JOB_PROCESS_CHANNEL,
    params: jobProcessId ? { job_process_id: jobProcessId } : undefined,
    enabled: !!jobProcessId,
    onReceived: (data) => {
      setStatus(data.status);

      const context = data.context as { progress?: JobProgress };
      if (context?.progress) setProgress(context.progress);

      if (data.status === JobProcessStatus.Completed || data.status === JobProcessStatus.Error) {
        subscription?.unsubscribe();
        onFinished?.();
      }
    },
  });

  return { progress, status };
}
